import { Link } from 'react-router-dom';

export default function CFIsStudentRoster() {
  const students = [
    {
      name: 'Sarah Mitchell',
      hours: 28.5,
      lastFlight: '2 days ago',
      progress: 72,
      skills: {
        landings: 85,
        navigation: 68,
        radio: 75,
        emergencies: 62
      },
      nextMilestone: 'Solo Flight',
      status: 'on-track'
    },
    {
      name: 'James Rodriguez',
      hours: 15.2,
      lastFlight: '5 days ago',
      progress: 45,
      skills: {
        landings: 52,
        navigation: 48,
        radio: 55,
        emergencies: 38
      },
      nextMilestone: 'Pattern Work',
      status: 'needs-attention'
    },
    {
      name: 'Emily Chen',
      hours: 42.8,
      lastFlight: '1 day ago',
      progress: 88,
      skills: {
        landings: 92,
        navigation: 85,
        radio: 90,
        emergencies: 86
      },
      nextMilestone: 'Checkride Prep',
      status: 'on-track'
    },
    {
      name: 'Michael Thompson',
      hours: 8.3,
      lastFlight: '3 days ago',
      progress: 28,
      skills: {
        landings: 35,
        navigation: 25,
        radio: 32,
        emergencies: 20
      },
      nextMilestone: 'Basic Maneuvers',
      status: 'on-track'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-6">
            <i className="ri-team-line text-teal-600 text-lg"></i>
            <span className="text-sm font-medium text-teal-700">Student Management</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Multi-Student Dashboard
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Track all your students in one place. Monitor progress, identify skill gaps, and prioritize your instruction time where it matters most.
          </p>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 p-8 mb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Your Students</h3>
              <p className="text-gray-600">4 active students • 94.7 total hours</p>
            </div>
            <div className="flex gap-3">
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-filter-3-line mr-2"></i>
                Filter
              </button>
              <button className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-add-line mr-2"></i>
                Add Student
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {students.map((student, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {student.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">{student.name}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {student.hours} hours
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          Last flight {student.lastFlight}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      student.status === 'on-track' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {student.status === 'on-track' ? (
                        <><i className="ri-check-line mr-1"></i>On Track</>
                      ) : (
                        <><i className="ri-alert-line mr-1"></i>Needs Attention</>
                      )}
                    </div>
                    <button className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                      <i className="ri-more-2-fill text-gray-600"></i>
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                    <span className="text-sm font-bold text-teal-600">{student.progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-teal-500 to-teal-600 rounded-full transition-all"
                      style={{ width: `${student.progress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-4">
                  {Object.entries(student.skills).map(([skill, score]) => (
                    <div key={skill} className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1 capitalize">{skill}</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              score >= 80 ? 'bg-green-500' : 
                              score >= 60 ? 'bg-amber-500' : 
                              'bg-red-500'
                            }`}
                            style={{ width: `${score}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-bold text-gray-900">{score}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-flag-line text-teal-600"></i>
                    <span>Next: <strong>{student.nextMilestone}</strong></span>
                  </div>
                  <div className="flex gap-2">
                    <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-file-list-3-line mr-2"></i>
                      View History
                    </button>
                    <button className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-play-circle-line mr-2"></i>
                      Start Debrief
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 border border-teal-100">
            <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-dashboard-line text-white text-2xl"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Real-Time Insights</h4>
            <p className="text-gray-600 text-sm">
              See which students need attention, who's ready for the next milestone, and where to focus your instruction time.
            </p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 border border-teal-100">
            <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-line-chart-line text-white text-2xl"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Skill Tracking</h4>
            <p className="text-gray-600 text-sm">
              Monitor progress across all ACS areas. Identify patterns and skill gaps before they become problems.
            </p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 border border-teal-100">
            <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-history-line text-white text-2xl"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Flight History</h4>
            <p className="text-gray-600 text-sm">
              Access complete flight records, debrief notes, and performance trends for every student in your roster.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}